import { type PageRect } from '@shared/board'
import { type TextLine } from '@shared/material'

// Turning a text selection on a drawn page into page geometry: the reader and
// clips on the board both let people select words and cut them out.

/** Selection rectangles merged into one box per line (a browser gives one per text run). */
export function lineBoxes(rects: DOMRect[]) {
  const out: { left: number; top: number; right: number; bottom: number }[] = []
  for (const r of [...rects].sort((a, b) => a.top - b.top || a.left - b.left)) {
    const same = out.find((o) => Math.abs(o.top - r.top) < r.height * 0.5)
    if (same) {
      same.left = Math.min(same.left, r.left)
      same.right = Math.max(same.right, r.right)
      same.bottom = Math.max(same.bottom, r.bottom)
    } else out.push({ left: r.left, top: r.top, right: r.right, bottom: r.bottom })
  }
  return out
}

/** The whole lines that `focus` falls on, with a little margin, or null when none are known. */
export function linesAround(lines: TextLine[], focus: PageRect[]): PageRect | null {
  const hit = lines.filter((l) =>
    focus.some(
      (f) =>
        Math.min(l.y + l.h, f.y + f.h) - Math.max(l.y, f.y) > Math.min(l.h, f.h) * 0.4 &&
        Math.min(l.x + l.w, f.x + f.w) > Math.max(l.x, f.x),
    ),
  )
  if (!hit.length) return null
  const x0 = Math.min(...hit.map((l) => l.x), ...focus.map((f) => f.x))
  const y0 = Math.min(...hit.map((l) => l.y), ...focus.map((f) => f.y))
  const x1 = Math.max(...hit.map((l) => l.x + l.w), ...focus.map((f) => f.x + f.w))
  const y1 = Math.max(...hit.map((l) => l.y + l.h), ...focus.map((f) => f.y + f.h))
  const pad = 0.008
  const x = Math.max(0, x0 - pad)
  const y = Math.max(0, y0 - pad)
  return { x, y, w: Math.min(1, x1 + pad) - x, h: Math.min(1, y1 + pad) - y }
}

export function fractionRect(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  box: DOMRect,
): PageRect {
  const cx = (v: number) => Math.min(1, Math.max(0, v))
  const x = cx((x0 - box.left) / box.width)
  const y = cx((y0 - box.top) / box.height)
  return {
    x,
    y,
    w: Math.max(0.002, cx((x1 - box.left) / box.width) - x),
    h: Math.max(0.002, cx((y1 - box.top) / box.height) - y),
  }
}
